// What a board can be. A canvas is the board as it has always been: widgets anywhere on an infinite
// plane. A typed board (a doc, a mind map, a calendar, a pipeline) brings its own
// surface and tools, keeps the scene, the chat, Connect and the Data panel, and holds its own content
// as entries (shared/entries.ts), never as items, so nothing that reads a canvas ever changes.
//
// A board's kind is chosen once, when it is made, and never changes (worker/workspace.ts cleanBoard).

import type { Board, BoardKind, EntryType } from "./types";

export interface KindSpec {
  kind: BoardKind;
  name: string;
  /** one line under its name in the new-board picker */
  blurb: string;
  /** a lucide icon, by name */
  icon: string;
  /**
   * Finished: shown in the new-board picker and made by agents. One that is not is made only on a dev
   * server (the picker's ?kinds=all), so a deploy never shows a kind half built.
   */
  ready: boolean;
  /** the entries a board of this kind holds; any other is refused */
  holds: EntryType[];
  /**
   * Why an agent's list or note (add_task, write_note) cannot go on it, or a widget at all yet, in words an
   * agent and a person read; null where they go anywhere, as on a canvas. A doc board places widgets in a
   * page (src/boards/kinds.ts place, and add_widget with page), and writes lists and notes into pages.
   */
  noWidgets: string | null;
  /** what an agent can do on it, a line read_board gives with each board of this kind */
  agents: string;
  /** a shared link shows it read only; one that cannot be shown yet cannot be shared */
  shareable: boolean;
  /** why a kind is never shared by link, when it is not for want of a view: said in Share, and by the server */
  noShare?: string;
}

export const BOARD_KINDS: Record<BoardKind, KindSpec> = {
  canvas: {
    kind: "canvas",
    name: "Canvas",
    blurb: "Widgets anywhere, kept current by your agents.",
    icon: "layout-dashboard",
    ready: true,
    holds: [],
    noWidgets: null,
    agents: "",
    shareable: true,
  },
  doc: {
    kind: "doc",
    name: "Doc",
    blurb: "Pages inside pages, with to-dos, toggles and live widgets.",
    icon: "file-text",
    ready: true,
    holds: ["page"],
    noWidgets: "A list or a note on a doc board is written into a page: write_page writes one (- [ ] for a to-do), and add_widget with page puts a widget in a page.",
    agents: "A doc board: its pages hold the writing, and its widgets sit inside pages. read_page reads a page as Markdown, write_page writes to one or makes one, remove_page moves one to the trash, and add_widget with page puts a widget in a page.",
    shareable: true,
  },
  mindmap: {
    kind: "mindmap",
    name: "Mind map",
    blurb: "Ideas as a map that you and your agent grow.",
    icon: "network",
    ready: true,
    holds: ["node"],
    // the map is drawn on the board's own canvas, and widgets float beside it as on any canvas
    noWidgets: null,
    agents: "A mind map board: one tree of ideas around its root idea, the board's topic, on a canvas with widgets beside it. read_map reads the tree as an indented outline with each idea's id; edit_map adds ideas under an idea, renames, moves, folds and removes them; add_widget puts a widget beside the map.",
    shareable: true,
  },
  calendar: {
    kind: "calendar",
    name: "Content calendar",
    blurb: "Plan, draft and track every post.",
    icon: "calendar-days",
    ready: true,
    holds: ["post"],
    // its widgets sit in a shelf beside the month, so an agent adds them as on a canvas
    noWidgets: null,
    agents: "A content calendar board: posts planned on days, each with a channel (x, linkedin, newsletter, blog or another name), a status (idea, draft, scheduled, posted) and its text, a thread as several parts; ideas have no day yet. read_posts reads them in full, write_post makes or changes one, remove_post moves one to the trash. Its widgets sit in a shelf beside the month. Nothing is published from the board: the user posts each one and marks it posted.",
    shareable: true,
  },
  pipeline: {
    kind: "pipeline",
    name: "Client pipeline",
    blurb: "Deals from first hello to won, with follow-ups.",
    icon: "square-kanban",
    ready: true,
    holds: ["deal", "stages"],
    // its widgets sit in a shelf beside the deals, in the order they were added
    noWidgets: null,
    agents: "A client pipeline board: deals in stages, each with its value, its next step and date, its history (calls, notes, what was agreed), and whether it was won or lost. write_deal makes or changes a deal and logs a note in one call, read_deal reads one with its whole history, and remove_deal moves one to the trash. update_board with stages renames its columns to suit the work, every deal staying in its column. Its widgets sit in a shelf beside the deals.",
    shareable: false,
    noShare: "A client pipeline holds other people's names and contact details, so it is never shared by link.",
  },
  // a calendar of the user's own events (shared/agenda.ts); its id is agenda, as calendar is the content calendar's
  agenda: {
    kind: "agenda",
    name: "Calendar",
    blurb: "Your meetings, posts and tasks in one place.",
    icon: "calendar",
    ready: true,
    holds: ["event", "calset"],
    noWidgets: "A Calendar board shows events and holds no widgets: put widgets on a canvas or a doc board.",
    agents: "A Calendar board: the user's events on their own clock, by day, week and month. Each event has a title, a start and an end (or whole days, all day), the time zone its times are kept in, and a place and notes when given. read_board lists the next seven days with each event's id, and read_calendar reads other days. add_task with day (and at and minutes) puts a task on it, as an event in its own calendar, and remove_task deletes one. write_event makes, moves, changes and cancels events, in its own calendar or the user's Google calendars, with guests and Google Meet, asking the user first before anything that reaches Google or cancels; find_time finds free time across every calendar it shows. It also shows the user's other boards as layers they can hide: a content calendar's posts and a pipeline's follow-ups, each with its board and its id there. Change one on its own board: a post with write_post, a follow-up with write_deal.",
    shareable: false,
    noShare: "A Calendar board shows your own events and plans, so it is not shared by link yet.",
  },
  // a sheet: tabs of cells and formulas, as Google Sheets and Excel keep them (shared/sheet/)
  sheet: {
    kind: "sheet",
    name: "Sheet",
    blurb: "Rows, columns and formulas, filled in with your agents.",
    icon: "table-2",
    ready: true,
    holds: ["sheet", "cells"],
    // its widgets sit in a shelf down its right side, as a pipeline's do
    noWidgets: null,
    agents: "A sheet board: tabs of cells, as in Google Sheets, each cell holding text, a number or a formula (=SUM(B2:B9)). read_sheet reads a tab as a table with its column letters and row numbers, the values as the user sees them, then each formula; write_sheet writes cells by address, rows of a table from a start cell, rows appended under the table, puts in and takes out rows and columns, clears a range, names ranges, makes or renames a tab, formats (borders too), merges, hides or sorts a range (by several columns), makes a range a table (append adds rows at its foot, which take its column formulas) and puts a chart of a range beside it, and says which filled cells it wrote over and which cells now show an error. Widgets go in the shelf down its right side.",
    shareable: true,
  },
  plan: {
    kind: "plan",
    name: "Plan",
    blurb: "Plan a big build with your agent, then watch it get built.",
    icon: "map",
    ready: true,
    holds: ["page", "question"],
    noWidgets: "A list or a note on a plan board is written into a page: write_page writes one (- [ ] for a task), and add_widget with page puts a widget in a page.",
    agents:
      "A plan board: the plan for a big build, made with the user before anything is built, then built by their coding agent, which keeps it up to date. The plan's name is the board's. Its first page is the Overview (the goal, who it is for, what done looks like, and a diagram of how the steps fit); every other top-level page is a step: what it does, how it works, its tasks as - [ ] to-dos, and the files it touches. read_plan reads the whole plan with each step's status, the user's answers and their notes; write_page writes a page (```mermaid draws a diagram), with parent for a step inside a step; ask_plan puts questions on it for the user to answer with a click, and wait_for_answer hands a coding agent the answer the moment it comes; a coding agent shows each question in its own session too, numbered, and records an answer the user types there with answer_plan; update_plan says how a step stands (planned, building, built with what proves it, blocked when it needs the user or waiting on someone outside the build, each with what it waits on and moved on once that has happened, later or dropped with why), ticks its tasks, notes a change to the plan, and carries a message: one or two plain sentences for the user, the way a colleague would message them, which the plan's chat shows. The board is where the user and their agents talk about the plan: questions go on it with ask_plan rather than in a chat, and a chat reply about it is a few lines saying what changed and what waits on the user there, never the plan again. A step's page is kept true to what is built: when a decision changes lines of it, write_page fixes them as update_plan notes the change. The coding agent sends any mockup it makes, and a screenshot of the result whenever a step changes how something looks, with update_plan's pictures: the user sees them in the plan's Previews. The board's own chat plans and never builds: when the plan is ready the user hands it to their coding agent with Hand off.",
    shareable: true,
  },
  files: {
    kind: "files",
    name: "Files",
    blurb: "Your project folders as notes you click through, linked and searchable.",
    icon: "folder-open",
    ready: true,
    // the files are the computer's: the board holds none of them, so nothing of it is kept as entries
    holds: [],
    noWidgets: "A Files board shows the project folders on the user's computer and holds no widgets: put widgets on a canvas or a doc board.",
    agents: "A Files board: the project folders on the user's computer, shown as notes to read, follow and edit. It holds nothing itself, so read_board gives no file's words: read and change the files where you reach them. The board's own chat has files tools there (list_files, read_file, search_files, and in a read and write folder write_file, edit_file, move_file, delete_file); an agent running on that computer uses its own. The board shows each change at once.",
    shareable: false,
    noShare: "A Files board shows the files on your own computer, so it is never shared by link.",
  },
  // a deck: one entry a slide, in order, and one for the deck's style (shared/slides.ts)
  slides: {
    kind: "slides",
    name: "Slides",
    blurb: "A deck you and your agents write, ready to present or share.",
    icon: "presentation",
    ready: true,
    holds: ["slide", "deck"],
    // a widget sits on a slide of its own, made with the Widget layout (src/boards/slides/place.ts, add_widget)
    noWidgets: null,
    agents: "A slides board: a deck, one slide after another, each with a layout (title, section, text, two-columns, big-number, quote, picture, full-picture, widget, blank), a title, its text, speaker notes, and a picture or a live widget. read_slides reads the deck as Markdown with each slide's number and id, each box on it, and the guide to designing slides once a session; write_slides writes a whole deck from Markdown (slides between --- lines), adds or replaces slides, moves them, sets a layout or the deck's style, and removes slides to the trash. Any slide is designed freely too: write_slides' boxes puts text boxes, shapes, lines and arrows, pictures and widgets anywhere on it, and moves or restyles its layout's title and text, places in percent of the slide; change only the boxes asked about, by id. boxes also lines boxes up and spaces them evenly (line_up, space_evenly with ids), and connects two boxes with a line or an elbow that stays attached (from_box, to_box). Boxes take PowerPoint's formats too: gradient and picture fills, outline dashes and ends, shadows, glows and soft edges, a picture's crop, crop to a shape and brightness, and a table's style, merged cells, rows and columns and cell borders; read_slides' guide names them. Charts and diagrams too: a chart box (chart_type, its data as a Markdown table, or sheet: {board, tab, range} to chart one of the user's sheets, which it then follows; only numbers the user gave or a sheet holds, never made up) and a diagram box (diagram: process, timeline, cycle, hierarchy or comparison, points a line each, indented for a level under the one above); asked to make a slide a timeline, the slide's points become a timeline diagram. add_widget with this board puts a live widget on a Widget slide of its own at the end of the deck. The deck as a whole: write_slides hides slides, puts them in sections, sets Morph between two slides, restyles the slide master and starts a deck from a template (pitch, weekly, kickoff, sales, lesson).",
    shareable: true,
  },
  // an app the user's agent writes, filling the board: a custom widget given the whole board and real records
  custom: {
    kind: "custom",
    name: "Custom",
    blurb: "A tool your agent builds to fit how you work.",
    icon: "wand-sparkles",
    ready: true,
    holds: ["record"],
    // the app is the one custom widget on it (add_widget type custom), drawn edge to edge in the canvas's place
    noWidgets: "A Custom board is one app that fills it: add_widget with type custom writes the app (once), and update_widget changes it. Its widgets are not placed beside it; put other widgets on a canvas or a doc board.",
    agents:
      "A Custom board: one app your agent writes, filling the board edge to edge, and its records. The app is a custom widget: call get_widget_guide (its section on a Custom board), then add_widget with type custom writes it, once; update_widget changes it (find and replace, or the whole html), as with any custom widget, and you hear how it drew. The records are what the user types into it: read_records reads them, write_records adds, changes and removes them, and the app shows each change at once. Hundreds fit; the app keeps its own choices (a filter, an open card) in board.save. To copy the board, create_board with from (its name): the app alone, or all its records too with records true.",
    shareable: false,
    noShare: "A Custom board holds your own tool and its records, so it is not shared by link yet.",
  },
};

/** Every kind, in the order the picker shows them. */
export const KIND_ORDER: BoardKind[] = ["canvas", "doc", "sheet", "slides", "plan", "files", "mindmap", "agenda", "calendar", "pipeline", "custom"];

/** Whether a board of this kind keeps its content in pages: a doc board, and a plan board, whose parts are pages. */
export function hasPages(kind: BoardKind): boolean {
  return kind === "doc" || kind === "plan";
}

export function isBoardKind(x: unknown): x is BoardKind {
  return typeof x === "string" && Object.hasOwn(BOARD_KINDS, x);
}

/** A board's kind: one made before boards had kinds, or left without one, is a canvas. */
export function kindOf(b: Pick<Board, "kind"> | null | undefined): BoardKind {
  return b?.kind && isBoardKind(b.kind) ? b.kind : "canvas";
}

/** Whether a board of this kind holds entries of this type. */
export function holds(kind: BoardKind, t: unknown): t is EntryType {
  return (BOARD_KINDS[kind].holds as string[]).includes(t as string);
}

/** The kinds an agent may make: the finished ones. */
export function readyKinds(): BoardKind[] {
  return KIND_ORDER.filter((k) => BOARD_KINDS[k].ready);
}
